﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

import * as React from "react";
import type { IntlContext } from "~base/Internationalization/IntlContext";
import { useIntl } from "~base/Internationalization/IntlContext";
import {
    buildReducerId,
    useConsilioReducer,
} from "~base/Utilities/ConsilioReducer";
import useStyles from "./Styles";
import type { SelfServiceViewAction } from "./Store";
import { initialState, reducer } from "./Store";
import type { SelfServiceViewState } from "./Types";
import { SelfServiceClaim, SelfServiceRegistrationPage } from "./Types";
import type { ApiStatus } from "~base/Services/API";
import { getErrorMessage } from "~base/Services/API";
import { useNavigate, useSearchParams } from "react-router-dom";
import ApplicationPage from "~base/Pages/Shared/ApplicationPage";
import SelfServiceHeaderMenu from "../Shared/SelfServiceHeaderMenu";
import PageAccount from "./InternalPages/PageAccount";
import PageClaims from "./InternalPages/PageClaims";
import PageAppRegistration from "./InternalPages/PageAppRegistration";
import type {
    PostSelfServiceRegistrationRequest,
    PostSelfServiceRegistrationResponseSuccess,
    SelfServiceRegistrationClaim,
    SelfServiceRegistrationTenant,
} from "~jrad/Services/API/Account/PostSelfServiceRegistration";
import { usePostSelfServiceRegistration } from "~jrad/Services/API/Account/PostSelfServiceRegistration";
import PageTermsOfService from "./InternalPages/PageTermsOfService";
import { getLocaleFromCookie } from "~base/Pages/Shared/LocalePicker";
import { useSettings } from "~base/Settings/SettingsContext";
import { useAuthToken } from "~base/Services/API/AuthToken";
import Overlay from "~base/Components/Composite/Overlay";
import TitleSelect from "~base/Components/Primitives/TitleSelect";
import { useGetTextCodeMaps } from "~base/Services/API/Resource/GetTextCodeMaps";
import {
    loadedIds,
    loadTextCodeMapCallback,
} from "~base/Services/TextCodeService";

//-------------------------------------------------------------------------------------
// Props
//-------------------------------------------------------------------------------------

type SelfServicePageComponentProps = unknown;

//-------------------------------------------------------------------------------------
// Component
//-------------------------------------------------------------------------------------

const SelfServicePageComponent: React.FunctionComponent<
    SelfServicePageComponentProps
> = (props) => {
    const intl = useIntl();
    const classes = useStyles();
    const navigate = useNavigate();
    const settings = useSettings();
    const serverRelativePath =
        settings.valueAsString("serverVirtualPath") ?? "/";
    const [searchParams] = useSearchParams();

    const [state, dispatch] = useConsilioReducer(
        buildReducerId("SelfServicePage"),
        (state: SelfServiceViewState, action: SelfServiceViewAction) =>
            reducer(state, action, intl),
        initialState
    );

    const [_, currentAuthToken] = useAuthToken({});

    React.useEffect(() => {
        if (state.responseData.IsRegistered) {
            navigate(
                "/Account/Confirm?username=" +
                    encodeURIComponent(state.requestData.email)
            );
        }
    }, [navigate, state.responseData.IsRegistered, state.requestData.email]);

    React.useEffect(() => {
        if (currentAuthToken?.access_token) {
            navigate("/Account/Logout");
        }
    }, [currentAuthToken, navigate]);

    const locale =
        getLocaleFromCookie() ??
        settings.valueAsString("account.defaultLanguage");

    const claims: SelfServiceRegistrationClaim[] = [];

    if (state.requestData.claims.firstName) {
        claims.push({
            Type: SelfServiceClaim.FirstName,
            Value: state.requestData.claims.firstName,
        });
    }
    if (state.requestData.claims.lastName) {
        claims.push({
            Type: SelfServiceClaim.LastName,
            Value: state.requestData.claims.lastName,
        });
    }
    if (state.requestData.claims.birthDate) {
        claims.push({
            Type: SelfServiceClaim.BirthDate,
            Value: state.requestData.claims.birthDate,
        });
    }

    const currentRequestData: PostSelfServiceRegistrationRequest = {
        Locale: locale,
        Email: state.requestData.email,
        Password: state.requestData.password,
        AccessCode: searchParams.get("AccessCode"),
        AccessKey: searchParams.get("AccessKey"),
        TenantUid:
            state.page === SelfServiceRegistrationPage.Account
                ? null
                : state.requestData.tenantUid,
        Claims: claims,
        AppRegistrationRequestId:
            state.page === SelfServiceRegistrationPage.AppRegistration
                ? state.responseData.AppRegistrationRequestId
                : null,
        IsPartial: !(
            state.page === SelfServiceRegistrationPage.EnterClaims ||
            (state.responseData.AllowAppRegistration &&
                state.page === SelfServiceRegistrationPage.AppRegistration)
        ),
    };

    const [__, getTextCodesApi] = useGetTextCodeMaps({
        callback: async (status, response, responseData) => {
            loadTextCodeMapCallback(
                serverRelativePath,
                status,
                dispatch,
                response,
                responseData
            );
        },
    });

    const [processingRegistrationRequest, postRegistrationRequest] =
        usePostSelfServiceRegistration({
            data: currentRequestData,
            call:
                state.verificationStatus === "Success" &&
                !state.responseData.IsRegistered &&
                !state.errorMessage,
            callback: async (
                status,
                response,
                responseData,
                requestData,
                context,
                traceContext
            ) => {
                const successResponseData =
                    responseData as PostSelfServiceRegistrationResponseSuccess;
                await callbackWithErrorHandling(
                    intl,
                    dispatch,
                    status,
                    response,
                    async () => {
                        const selectedTenantId = state.requestData.tenantUid;
                        const tenant =
                            successResponseData.Tenants &&
                            successResponseData.Tenants.find(
                                (x) =>
                                    x.TenantUid === selectedTenantId ||
                                    successResponseData.Tenants.length == 1
                            );
                        if (tenant) {
                            const ids = loadedIds();

                            dispatch({ type: "waitForTextCodesToLoad" });

                            await getTextCodesApi({
                                keys: ids,
                                locale: locale,
                                tenant: tenant.TenantName,
                            });
                        }

                        dispatch({
                            type: "setResponseData",
                            data: responseData,
                        });
                    }
                );
            },
        });

    const triggerRegistrationRequest = () => {
        postRegistrationRequest(currentRequestData);
    };

    const isLoading =
        processingRegistrationRequest ||
        state.responseData.IsRegistered ||
        state.waitForTextCodesToLoad;

    React.useEffect(() => {
        if (!isLoading && state.page != state.nextPageAfterLoading) {
            dispatch({
                type: "setCurrentPage",
                page: state.nextPageAfterLoading,
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, state.page, state.nextPageAfterLoading]);

    const overlayContainer = document.getElementById("react-portal-root");

    const tenants = state.responseData.Tenants;

    return (
        <>
            <ApplicationPage
                isLoading={isLoading}
                headerMenu={<SelfServiceHeaderMenu />}
                centerVertical={false}
                handleMargins={false}
                contentWrapperClassName={classes.pageContainer}
            >
                <IntroDescription
                    state={state}
                    dispatch={dispatch}
                    tenants={tenants}
                />
                {state.errorMessage && (
                    <p className={classes.errorMessage}>{state.errorMessage}</p>
                )}
                <hr className={classes.introDivider} />
                <InternalPage
                    isLoading={isLoading}
                    state={state}
                    dispatch={dispatch}
                    postRegistrationRequest={triggerRegistrationRequest}
                    currentRequestData={currentRequestData}
                    tenants={tenants}
                />
            </ApplicationPage>
            <Overlay
                testIdentifier="SelectTenant"
                handleOverflow
                open={
                    state.page === SelfServiceRegistrationPage.SelectTenant &&
                    !isLoading
                }
                overlayContainer={overlayContainer}
                titleTextCode="VisiFinance.UI.SelfServiceRegister.SelectTenant.Header"
                showAbortButton={false}
                saveButtonTitleTextCode="VisiFinance.UI.SelfServiceRegister.NextButton"
                onCancel={() => {
                    if (!isLoading) {
                        dispatch({
                            type: "setRequestData",
                            key: "tenantUid",
                            value: null,
                        });
                        dispatch({
                            type: "setCurrentPage",
                            page: SelfServiceRegistrationPage.Account,
                        });
                    }
                }}
                onSave={triggerRegistrationRequest}
                saveButtonEnabled={!!state.requestData.tenantUid && !isLoading}
                runOnCancelAfterOnSave={false}
            >
                <p className={classes.overlayDescription}>
                    {intl.formatText(
                        "VisiFinance.UI.SelfServiceRegister.SelectTenant.Message"
                    )}
                </p>
                <TitleSelect
                    testIdentifier="VisiFinance.UI.SelfServiceRegister.SelectTenant.Label"
                    required
                    titleTextCode="VisiFinance.UI.SelfServiceRegister.SelectTenant.Label"
                    value={state.requestData.tenantUid}
                    stringValues={state.responseData.Tenants.map((tenant) => ({
                        displayName: tenant.TenantName,
                        value: tenant.TenantUid,
                    }))}
                    onChange={(selectedTenantUid) => {
                        dispatch({
                            type: "setRequestData",
                            key: "tenantUid",
                            value: selectedTenantUid,
                        });
                    }}
                />
            </Overlay>
        </>
    );
};

//-------------------------------------------------------------------------------------
// Private functions
//-------------------------------------------------------------------------------------

interface SelfServiceInternalPageProps {
    isLoading: boolean;
    state: SelfServiceViewState;
    dispatch: React.Dispatch<SelfServiceViewAction>;
    postRegistrationRequest: () => void;
    currentRequestData: PostSelfServiceRegistrationRequest;
    tenants: SelfServiceRegistrationTenant[];
}

const InternalPage: React.FunctionComponent<SelfServiceInternalPageProps> = (
    props
) => {
    switch (props.state.page) {
        case SelfServiceRegistrationPage.Account:
        case SelfServiceRegistrationPage.SelectTenant: {
            return <PageAccount {...props} />;
        }
        case SelfServiceRegistrationPage.TermsOfService: {
            return <PageTermsOfService {...props} />;
        }
        case SelfServiceRegistrationPage.EnterClaims: {
            return <PageClaims {...props} />;
        }
        case SelfServiceRegistrationPage.AppRegistration: {
            return <PageAppRegistration {...props} />;
        }
    }
};

interface SelfServiceIntroDescriptionProps {
    state: SelfServiceViewState;
    dispatch: React.Dispatch<SelfServiceViewAction>;
    tenants: SelfServiceRegistrationTenant[];
}

const IntroDescription: React.FunctionComponent<
    SelfServiceIntroDescriptionProps
> = ({ state, dispatch, tenants }) => {
    const intl = useIntl();
    const classes = useStyles();
    const settings = useSettings();

    const registrationTenant =
        tenants && tenants.length == 1 ? tenants[0].TenantName : null;
    const tenantName = settings.valueAsString("detectedTenantName");
    const tenantDiplayName =
        intl.tryFormatText("VisiFinance.UI.TenantDisplayName") ??
        registrationTenant ??
        tenantName;

    switch (state.page) {
        case SelfServiceRegistrationPage.Account:
        case SelfServiceRegistrationPage.SelectTenant: {
            return (
                <>
                    <h1 className={classes.introHeader}>
                        {intl.formatText(
                            "VisiFinance.UI.SelfServiceRegister.Title"
                        )}
                    </h1>
                    <p
                        className={classes.introDesc}
                        dangerouslySetInnerHTML={{
                            __html: intl.formatText(
                                "VisiFinance.UI.SelfServiceRegister.WelcomeMessage"
                            ),
                        }}
                    />
                </>
            );
        }
        case SelfServiceRegistrationPage.AppRegistration: {
            return (
                <>
                    <h1 className={classes.introHeader}>
                        {intl.formatText(
                            "VisiFinance.UI.SelfServiceRegister.Verification.Title"
                        )}
                    </h1>
                    <p
                        className={classes.introDesc}
                        dangerouslySetInnerHTML={{
                            __html: intl.formatText(
                                "VisiFinance.UI.SelfServiceRegister.Verification.Message",
                                state.requestData.email
                            ),
                        }}
                    />
                    {state.responseData.AllowManualRegistration && (
                        <p className={classes.introDesc}>
                            <span>
                                {intl.formatText(
                                    "VisiFinance.UI.SelfServiceRegister.Verification.Manual.Pre"
                                ) + " "}
                            </span>
                            <a
                                href="#"
                                onClick={(ev) => {
                                    ev.preventDefault();
                                    ev.stopPropagation();

                                    dispatch({
                                        type: "setCurrentPage",
                                        page: SelfServiceRegistrationPage.EnterClaims,
                                    });
                                }}
                            >
                                {intl.formatText(
                                    "VisiFinance.UI.SelfServiceRegister.Verification.Manual.Link"
                                )}
                            </a>
                            <span>
                                {" " +
                                    intl.formatText(
                                        "VisiFinance.UI.SelfServiceRegister.Verification.Manual.Post"
                                    )}
                            </span>
                        </p>
                    )}
                </>
            );
        }
    }

    const introDescFQN = "VisiFinance.UI.SelfServiceRegister.About.Description";

    const introHeaderFQN = "VisiFinance.UI.SelfServiceRegister.About.Header";

    return (
        <>
            <h1 className={classes.introHeader}>
                {intl.formatText(introHeaderFQN, ...[tenantDiplayName])}
            </h1>
            <p className={classes.introDesc}>
                {intl.formatText(introDescFQN, ...[tenantDiplayName])}
            </p>
        </>
    );
};

const callbackWithErrorHandling = async (
    intl: IntlContext,
    dispatch: React.Dispatch<SelfServiceViewAction>,
    status: ApiStatus,
    response: Response,
    successAction: () => Promise<void>
) => {
    if (status == "Success") {
        await successAction();
    } else {
        dispatch({
            type: "fetchError",
            message: await getErrorMessage(intl, response),
        });
    }
};

export default SelfServicePageComponent;
